import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePassProducts } from '../../hooks/passes/usePassProducts'
import { usePurchasedPasses } from '../../hooks/passes/usePurchasedPasses'
import { PassPurchaseLocationState, usePassPurchase } from '../../hooks/passes/usePassPurchase'

import Loading from '../../components/loading/Loading'
import { Tabs } from '../../components/tabs/Tabs'
import PageHeader from '../../components/ui/pageHeader/PageHeader'
import { PassProductCard } from '../../components/authenticated/passes/passProductCard/PassProductCard'
import { PurchasedPassList } from '../../components/authenticated/passes/purchasedPassList/PurchasedPassList'

import { useApp } from '../../contexts/AppContext'
import { BannerKind } from '../../types/contexts/AppContext'
import { PassProduct, PurchasedPass, PurchasedPassStatus } from '../../types/booking/booking'
import { formatMinutes } from '../../utils/booking/booking'

/** Prepaid passes: buy minutes and see the passes owned (active first; history on demand). */
const Passes = () => {
  const { t } = useTranslation('passes')
  const navigate = useNavigate()
  const location = useLocation()
  const { showBanner } = useApp()
  const products = usePassProducts()
  const purchased = usePurchasedPasses()
  const { buy } = usePassPurchase('/passes')
  const [onSale, setOnSale] = useState<PassProduct[] | null>(null)
  const [mine, setMine] = useState<PurchasedPass[] | null>(null)
  const [showAll, setShowAll] = useState(true)
  const resumed = useRef(false)

  useEffect(() => {
    products
      .list()
      .then(result => setOnSale(result ?? []))
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setMine(null)
    purchased
      .mine(showAll)
      .then(result => setMine(result ?? []))
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll])

  useEffect(() => {
    // Back from completing the billing profile: continue the purchase that was interrupted.
    const productId = (location.state as PassPurchaseLocationState | null)?.buyPassProductId
    const product = onSale?.find(item => item.id === productId)
    if (product && !resumed.current) {
      resumed.current = true
      navigate(location.pathname, { replace: true, state: null })
      buy(product)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSale])

  const availableMinutes = (mine ?? [])
    .filter(pass => pass.status === PurchasedPassStatus.ACTIVE)
    .reduce((sum, pass) => sum + pass.availableMinutes, 0)

  return (
    <>
      <PageHeader
        title={{
          eyebrow: mine ? t('passes.page-header.available', { minutes: formatMinutes(availableMinutes) }) : undefined,
          h1: t('passes.page-header.title'),
        }}
        sub={t('passes.page-header.sub')}
      />
      <div className="row g-4">
        <div className="col-12">
          <h3 className="mb-3">{t('passes.on-sale')}</h3>
          {onSale === null ? (
            <Loading variant="inline" />
          ) : onSale.length === 0 ? (
            <div className="empty muted">{t('passes.none-on-sale')}</div>
          ) : (
            <div className="row g-3">
              {onSale.map(product => (
                <div className="col-12 col-md-6 col-xxl-4" key={product.id}>
                  <PassProductCard
                    product={product}
                    actionLabel={t('passes.card.buy')}
                    onAction={buy}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="col-12">
          <div className="ph-row ph-between mb-3 flex-wrap gap-2">
            <h3 className="m-0">{t('passes.mine.title')}</h3>
            <Tabs
              tab={showAll ? 'all' : 'active'}
              tabs={[
                { value: 'all', label: t('passes.mine.filter.all') },
                { value: 'active', label: t('passes.mine.filter.active') },
              ]}
              onChange={value => setShowAll(value === 'all')}
            />
          </div>
          {mine === null ? (
            <Loading variant="inline" />
          ) : mine.length === 0 ? (
            <div className="empty muted">{t('passes.mine.empty')}</div>
          ) : (
            <PurchasedPassList passes={mine} />
          )}
        </div>
      </div>
    </>
  )
}

export default Passes
